import { Form, Formik } from 'formik'
import { FC, useState } from 'react'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import { FormattedMessage, useIntl } from 'react-intl'

import { QueueCountersAPI } from '@api/queueCountersAPI'

import { Option } from '@models/option'

import { Button } from '@components/Button'
import { FormControl } from '@components/FormControl'
import { KTCard } from '@components/KTCard'

import { useAuth } from '@modules/auth/contexts/AuthContext'
import { PermissionsControl } from '@modules/permissions'
import { useQueueMenu } from '@modules/queues/contexts/QueueMenuContext'

import { PermissionEnum } from '@/constants/permission'
import { useAlert } from '@/hooks'
import AxiosUtil from '@/utils/axiosUtil'

import { CounterListSelectField } from './fields/CounterListSelectField'
import { QueueListSelectField } from './fields/QueueListSelectField'
import { ID } from '@models/base'

export type CheckInFormShape = {
  queue_id: Option
  counter_id: Option
}

const QueueCheckinCounterForm: FC = () => {
  const intl = useIntl()
  const alert = useAlert()
  const [error, setError] = useState<string>()

  const { refetchQueueProcess } = useQueueMenu()
  const { refetchAuth } = useAuth()

  return (
    <Formik<Partial<CheckInFormShape>>
      initialValues={{
        queue_id: undefined,
        counter_id: undefined,
      }}
      validate={(values) => {
        const errors: { [K in keyof CheckInFormShape]?: string } = {}
        if (!values.queue_id) {
          // intentional empty string, just to prevent submit button using formik.isValid
          errors.queue_id = ''
        }
        if (!values.counter_id) {
          errors.counter_id = ''
        }
        return errors
      }}
      onSubmit={async ({ counter_id }, { setSubmitting }) => {
        setSubmitting(true)
        try {
          await QueueCountersAPI.checkIn(counter_id?.value as ID)

          refetchAuth()
          refetchQueueProcess()

          alert.success({
            text: intl.formatMessage({ id: 'queue.check_in_success' }),
          })
        } catch (err) {
          if (AxiosUtil.isAxiosError(err)) {
            setError(err.response?.data.message)

            console.error(err)
          }
        } finally {
          setSubmitting(false)
        }
      }}
    >
      {(formik) => (
        <>
          <KTCard.Header className="border-0">
            <KTCard.Title>
              <FormattedMessage id="queue.counter.check_in" />
            </KTCard.Title>
          </KTCard.Header>

          <KTCard.Body>
            <Form
              id="open_counter_form"
              className="form"
              onSubmit={formik.handleSubmit}
            >
              {/* begin::Alert */}
              {error && <div className="alert alert-danger d-flex align-items-center p-5 mb-10">{error}</div>}
              {/* end::Alert */}

              <Row>
                {/* Begin: Queue and queue counter */}
                <Col xl={12}>
                  <FormControl>
                    <FormControl.Label isRequired>
                      <FormattedMessage id="queue" />
                    </FormControl.Label>
                    <QueueListSelectField
                      required
                      name="queue_id"
                    />
                    <FormControl.ErrorMessage message={formik.errors.queue_id} />
                  </FormControl>
                </Col>

                {formik.values.queue_id && (
                  <Col xl={12}>
                    <FormControl>
                      <FormControl.Label isRequired>
                        <FormattedMessage id="queue.counter" />
                      </FormControl.Label>
                      <CounterListSelectField
                        required
                        name="counter_id"
                      />
                      <FormControl.ErrorMessage message={formik.errors.counter_id} />
                    </FormControl>
                  </Col>
                )}
                {/* End: Queue and queue counter */}
              </Row>

              {/* begin::Actions */}
              <div className="text-center pt-10">
                <PermissionsControl
                  allow={PermissionEnum.QUEUE_COUNTER_CHECK_IN}
                  renderError
                >
                  <Button
                    type="submit"
                    theme="primary"
                    className="w-100"
                    isLoading={formik.isSubmitting}
                    disabled={!formik.isValid || !formik.dirty}
                  >
                    <span className="indicator-label">Check In</span>
                  </Button>
                </PermissionsControl>
              </div>
              {/* end::Actions */}
            </Form>
          </KTCard.Body>
        </>
      )}
    </Formik>
  )
}

export { QueueCheckinCounterForm }
